import { Loader2, X } from 'lucide-react';
import React, { useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { useGetTasksQuery } from '@/services/api/tasksService';
import {
  CanonicalTaskGroup,
} from '@/services/types/task-types';

import { CompactTaskGroup } from './CompactTaskGroup';
import { CreateTaskModal } from './CreateTaskModal';
import { useTaskGrouping } from './useTaskGrouping';

type FilterOption = {
  key: string;
  label: string;
  groups: CanonicalTaskGroup[] | null;
};

// High-level filter options that can map to one or many canonical groups
const FILTER_OPTIONS: FilterOption[] = [
  { key: 'ALL', label: 'All types', groups: null },
  {
    key: 'CLAIMS',
    label: 'Claims',
    groups: [
      CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
      CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
    ],
  },
  {
    key: 'INTAKE',
    label: 'Intake',
    groups: [CanonicalTaskGroup.INTAKE],
  },
  {
    key: 'INITIAL_REVIEW',
    label: 'Initial Review',
    groups: [CanonicalTaskGroup.INITIAL_REVIEW],
  },
  {
    key: 'POLICE',
    label: 'Police Report & Investigation',
    groups: [CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION],
  },
  {
    key: 'MEDICAL',
    label: 'Medical Records',
    groups: [CanonicalTaskGroup.MEDICAL_RECORDS],
  },
  {
    key: 'TREATMENT',
    label: 'Treatment',
    groups: [CanonicalTaskGroup.TREATMENT],
  },
  {
    key: 'DEMAND_LETTER',
    label: 'Demand Letter',
    groups: [CanonicalTaskGroup.DEMAND_LETTER],
  },
  { key: 'LIENS', label: 'Liens', groups: [CanonicalTaskGroup.LIENS] },
  {
    key: 'ADDITIONAL_INSURANCE',
    label: 'Additional Insurance',
    groups: [CanonicalTaskGroup.ADDITIONAL_INSURANCE],
  },
  {
    key: 'MISC',
    label: 'Miscellaneous',
    groups: [CanonicalTaskGroup.MISCELLANEOUS],
  },
];

const TaskLoader = () => (
  <div className="flex justify-center items-center py-8 h-full w-full">
    <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
  </div>
);

interface TaskGroupingsProps {
  onOpenCreateTask?: () => void;
  /** Search term for task filtering (managed by parent) */
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
}

export const TaskGroupings: React.FC<TaskGroupingsProps> = ({
  onOpenCreateTask,
  searchTerm: externalSearchTerm,
  onSearchTermChange,
}) => {
  const { matter_id } = useParams();
  const location = useLocation();

  // Search state — use external if provided, otherwise internal
  const [internalSearchTerm, setInternalSearchTerm] = React.useState('');
  const searchTerm = externalSearchTerm ?? internalSearchTerm;
  const setSearchTerm = onSearchTermChange ?? setInternalSearchTerm;
  const debouncedSearch = useDebouncedValue(searchTerm, 200);
  const searchTermRef = React.useRef(searchTerm);
  searchTermRef.current = searchTerm;

  // Multi-select filter keys (empty array = ALL)
  const [selectedFilterKeys, setSelectedFilterKeys] = React.useState<string[]>(
    [],
  );

  // Toggle a filter on/off
  const toggleFilter = useCallback((key: string) => {
    setSelectedFilterKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedFilterKeys([]);
  }, []);

  // Detect which tab is active based on URL path
  const activeTab = React.useMemo(() => {
    const pathParts = location.pathname.split('/');
    const tabSegment = pathParts[3]; // e.g., /matters/:id/coverage -> 'coverage'
    if (tabSegment === 'coverage') return 'coverage';
    if (tabSegment === 'treatment') return 'treatment';
    if (tabSegment === 'police') return 'police';
    if (!tabSegment || tabSegment === '') return 'home';
    return 'other';
  }, [location.pathname]);

  const lastAutoFilteredTabRef = React.useRef<string | null>(null);

  // Auto-filter based on active tab
  React.useEffect(() => {
    // Only auto-filter if we haven't already for this tab
    if (lastAutoFilteredTabRef.current === activeTab) return;

    switch (activeTab) {
      case 'coverage':
        setSelectedFilterKeys(['CLAIMS']);
        lastAutoFilteredTabRef.current = activeTab;
        break;
      case 'treatment':
        // Select both Treatment and Medical Records for treatment tab
        setSelectedFilterKeys(['TREATMENT', 'MEDICAL']);
        lastAutoFilteredTabRef.current = activeTab;
        break;
      case 'police':
        setSelectedFilterKeys(['POLICE']);
        lastAutoFilteredTabRef.current = activeTab;
        break;
      case 'home':
        setSelectedFilterKeys([]);
        lastAutoFilteredTabRef.current = activeTab;
        break;
      default:
        // For other tabs, don't auto-filter but track the tab
        lastAutoFilteredTabRef.current = activeTab;
        break;
    }
  }, [activeTab]);

  // Combine selected filter groups into a single array
  const selectedGroups = React.useMemo(() => {
    if (selectedFilterKeys.length === 0) return null; // null means ALL
    const groups: CanonicalTaskGroup[] = [];
    selectedFilterKeys.forEach((key) => {
      const option = FILTER_OPTIONS.find((opt) => opt.key === key);
      if (option?.groups) {
        groups.push(...option.groups);
      }
    });
    return groups.length > 0 ? groups : null;
  }, [selectedFilterKeys]);
  const { data: tasks, isLoading: isTasksLoading } = useGetTasksQuery(
    {
      limit: 100,
      matter: matter_id,
      ...(selectedGroups ? { canonical_groups: selectedGroups } : {}),
    },
    {
      pollingInterval: 100000,
      skipPollingIfUnfocused: true,
    },
  );

  // Client-side search filtering (task name only)
  const filteredResults = React.useMemo(() => {
    if (!tasks?.results || !debouncedSearch.trim()) return tasks?.results;
    const term = debouncedSearch.toLowerCase().trim();
    return tasks.results.filter((task) =>
      task.name.toLowerCase().includes(term),
    );
  }, [tasks?.results, debouncedSearch]);

  const { allTasks, getFilteredAndSortedGroups, getInitialOpenState } =
    useTaskGrouping(filteredResults);

  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    {},
  );
  const [showCompletedGroups, setShowCompletedGroups] = React.useState(true);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] =
    React.useState(false);
  const initializedRef = React.useRef(false);

  // Initialize open state when tasks load
  React.useEffect(() => {
    if (tasks?.results && !initializedRef.current) {
      setOpenGroups(getInitialOpenState());
      initializedRef.current = true;
    }
  }, [tasks?.results, getInitialOpenState]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd/Ctrl + K to create new task
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsCreateTaskModalOpen(true);
      }

      // Cmd/Ctrl + Shift + H to toggle completed groups
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.key === 'H'
      ) {
        event.preventDefault();
        setShowCompletedGroups((prev) => !prev);
      }

      // Escape to clear search
      if (event.key === 'Escape' && searchTermRef.current) {
        event.preventDefault();
        setSearchTerm('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-expand all groups when entering search, restore defaults when cleared
  const isSearching = debouncedSearch.trim().length > 0;
  const prevIsSearchingRef = React.useRef(false);
  React.useEffect(() => {
    if (isSearching && !prevIsSearchingRef.current) {
      // Entering search mode: expand all groups so matches are visible
      const groups = getFilteredAndSortedGroups(true);
      const allOpen: Record<string, boolean> = {};
      groups.forEach(([groupName]) => {
        allOpen[groupName] = true;
      });
      setOpenGroups(allOpen);
    } else if (!isSearching && prevIsSearchingRef.current && initializedRef.current) {
      // Leaving search mode: restore default open/close state
      setOpenGroups(getInitialOpenState());
    }
    prevIsSearchingRef.current = isSearching;
  }, [isSearching, getFilteredAndSortedGroups, getInitialOpenState]);

  const toggleGroup = useCallback((groupName: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  const handleCreateTask = useCallback(() => {
    if (onOpenCreateTask) {
      onOpenCreateTask();
    } else {
      setIsCreateTaskModalOpen(true);
    }
  }, [onOpenCreateTask]);

  if (isTasksLoading) {
    return <TaskLoader />;
  }

  const isFiltered = selectedFilterKeys.length > 0;
  const filteredGroups = getFilteredAndSortedGroups(showCompletedGroups);

  // Get filter label for empty state messaging
  const getFilterLabel = () => {
    if (selectedFilterKeys.length === 0) return null;
    const labels = selectedFilterKeys
      .map((key) => FILTER_OPTIONS.find((opt) => opt.key === key)?.label)
      .filter(Boolean);
    return labels.join(' & ').toLowerCase();
  };

  // Determine which empty state to show
  const renderEmptyState = () => {
    // Case 0: Search active but no tasks match
    if (isSearching && (!allTasks || allTasks.length === 0)) {
      return (
        <div className="flex items-center justify-center py-8 bg-white">
          <div className="text-center">
            <span className="text-sm text-slate-400">
              No tasks matching &ldquo;{debouncedSearch}&rdquo;
            </span>
            <div className="mt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchTerm('')}
                className="text-xs text-slate-500"
              >
                Clear search
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Case 1: Filtered but no tasks match the filter
    if (isFiltered && (!allTasks || allTasks.length === 0)) {
      return (
        <div className="flex items-center justify-center py-8 bg-white">
          <div className="text-center">
            <span className="text-sm text-slate-400">
              No {getFilterLabel()} tasks
            </span>
            <div className="mt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs text-slate-500"
              >
                Show all tasks
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Case 2: No tasks at all (unfiltered)
    if (!allTasks || allTasks.length === 0) {
      return (
        <div className="flex items-center justify-center py-8 bg-white">
          <div className="text-center">
            <span className="text-sm text-slate-400">No tasks yet</span>
            <div className="mt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCreateTask}
                className="text-xs text-slate-500"
              >
                Create a task
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Case 3: Tasks exist but all are completed (filtered out)
    return (
      <div className="flex items-center justify-center py-8 bg-white">
        <div className="text-center">
          <span className="text-sm text-slate-400">All tasks completed</span>
          <div className="mt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCompletedGroups(true)}
              className="text-xs text-slate-500"
            >
              Show completed tasks
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full bg-slate-50">
      {/* Filter - Multi-select chips */}
      <div className="bg-white px-3 py-2 border-b border-zinc-100">
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTER_OPTIONS.filter((opt) => opt.key !== 'ALL').map((opt) => {
            const isSelected = selectedFilterKeys.includes(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => toggleFilter(opt.key)}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors',
                  isSelected
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
                )}
              >
                {opt.label}
                {isSelected && <X className="h-2.5 w-2.5" />}
              </button>
            );
          })}
          {selectedFilterKeys.length > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[10px] text-zinc-500 hover:text-zinc-700 ml-1"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Task Groups */}
      <div className="bg-white">
        {filteredGroups.map(([groupName, groupTasks]) => (
          <CompactTaskGroup
            key={groupName}
            name={groupName}
            tasks={groupTasks}
            isOpen={openGroups[groupName] || false}
            onToggle={() => toggleGroup(groupName)}
            matterId={matter_id || ''}
            showUrgentOnly={false}
            urgentTaskIds={new Set()}
            searchTerm={debouncedSearch}
          />
        ))}

        {filteredGroups.length === 0 && renderEmptyState()}
      </div>

      {/* Create Task Modal - only render if not using external handler */}
      {!onOpenCreateTask && (
        <CreateTaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
        />
      )}
    </div>
  );
};
